import { authStore } from './auth-store'

export interface ApiClientConfig {
  baseUrl: string
  apiKey: string
  /** Admin-mintet, kortlivet preview-token (widget-bygger live preview). Sendes
   *  som X-Preview-Token; serveren resolver tenant fra det og springer api-key/
   *  origin-tjekket over. Udelades i normal drift. */
  previewToken?: string
}

export interface ApiResponse<T = unknown> {
  data?: T
  error?: string
  code?: string
  /** Serverens forslag til en rettet e-mailadresse (EMAIL_DOMAIN_NO_MAIL, F-T14-3). */
  suggestion?: string
  /** FIRST_CHARGE_CHANGED: den rigtige første betaling (kr), checkout ville trække. */
  firstChargeDkk?: number
}

class ApiClient {
  private config: ApiClientConfig | null = null
  // Shared init-data promise — when multiple <holdstyring-*> custom elements
  // are on the same page they all need the same /api/widget/init payload.
  // Without this dedup we'd fire one init request per widget instance (e.g.
  // 23 identical POSTs on a kitchen-sink page), which both stalls the browser
  // and pegs the server. Keyed by apiKey so multi-tenant pages still work.
  private initPromiseByKey = new Map<string, Promise<ApiResponse<unknown>>>()

  init(config: ApiClientConfig) {
    this.config = config
  }

  /**
   * The host this widget actually talks to, e.g. "https://studio.example.com".
   *
   * Widgets need it to build links (book, prices, gift cards). Before this
   * existed every widget hardcoded `https://<slug>.holdstyring.dk`, so a studio
   * on its own domain sent visitors to a Holdstyring-branded URL even though
   * the API calls already honoured `base-url`. Returns '' before init.
   */
  getBaseUrl(): string {
    return this.config?.baseUrl ?? ''
  }

  /** The tenant's widget API key, for callers that build their own fetch (e.g.
   *  the retreat booking modal, which needs to read error `details` that the
   *  generic post() helper discards). Returns '' before init. */
  getApiKey(): string {
    return this.config?.apiKey ?? ''
  }

  /**
   * Returns a currently-valid access token, refreshing it first if it is about
   * to expire. Returns null when the visitor is not logged in (no refresh
   * token) or the refresh failed. Use before a self-built authorized fetch so a
   * logged-in visitor is attached to the request without sending a stale token
   * (which some endpoints reject outright rather than treating as a guest).
   */
  async getFreshAccessToken(): Promise<string | null> {
    if (!this.config) return null
    const ok = await this.refreshTokenIfNeeded()
    return ok ? authStore.getAccessToken() : null
  }

  /**
   * Shared init-data fetcher. Multiple callers within the same browser tab
   * with the same apiKey see a single /api/widget/init round-trip.
   *
   * Cleared on auth-token refresh failure so a re-init can recover from
   * stale state. The cache is intentionally per-apiKey rather than global —
   * multi-tenant pages would otherwise leak data across keys.
   */
  async getInitData<T>(): Promise<ApiResponse<T>> {
    if (!this.config) return { error: 'ApiClient not initialized', code: 'NOT_INITIALIZED' }
    const key = this.config.apiKey
    let pending = this.initPromiseByKey.get(key) as Promise<ApiResponse<T>> | undefined
    if (!pending) {
      pending = this.post<T>('/api/widget/init', {})
      this.initPromiseByKey.set(key, pending as Promise<ApiResponse<unknown>>)
      // Drop the cache on error so a later widget can retry, but keep
      // successful results so subsequent widget mounts skip the round-trip.
      pending.then(result => {
        if (result.error) this.initPromiseByKey.delete(key)
      }).catch(() => this.initPromiseByKey.delete(key))
    }
    return pending
  }

  private getHeaders(includeAuth = false): HeadersInit {
    if (!this.config) {
      throw new Error('ApiClient not initialized. Call init() first.')
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }
    // Preview-token tager forrang (read-only admin preview); ellers api-key.
    if (this.config.previewToken) {
      headers['X-Preview-Token'] = this.config.previewToken
    }
    if (this.config.apiKey) {
      headers['X-API-Key'] = this.config.apiKey
    }

    if (includeAuth) {
      const token = authStore.getAccessToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const data = await response.json()

    if (!response.ok) {
      return {
        error: data.error || 'Request failed',
        code: data.code || 'UNKNOWN_ERROR',
        ...(typeof data.suggestion === 'string' && data.suggestion ? { suggestion: data.suggestion } : {}),
        ...(typeof data.firstChargeDkk === 'number' ? { firstChargeDkk: data.firstChargeDkk } : {}),
      }
    }

    return { data }
  }

  private async refreshTokenIfNeeded(): Promise<boolean> {
    const accessToken = authStore.getAccessToken()
    const refreshToken = authStore.getRefreshToken()

    if (!refreshToken) return false

    // Check if access token is expired or will expire in next 60 seconds
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]))
        const expiresAt = payload.exp * 1000
        if (expiresAt - Date.now() > 60000) {
          return true // Token still valid
        }
      } catch {
        // Token parsing failed, try to refresh
      }
    }

    // Refresh the token
    const response = await fetch(`${this.config!.baseUrl}/api/widget/auth/refresh`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ refreshToken }),
    })

    if (!response.ok) {
      authStore.clearTokens()
      return false
    }

    const data = await response.json()
    authStore.setTokens(data.accessToken, data.refreshToken)
    return true
  }

  async get<T>(endpoint: string, requireAuth = false): Promise<ApiResponse<T>> {
    if (!this.config) {
      return { error: 'ApiClient not initialized', code: 'NOT_INITIALIZED' }
    }

    if (requireAuth) {
      const tokenValid = await this.refreshTokenIfNeeded()
      if (!tokenValid) {
        return { error: 'Authentication required', code: 'AUTH_REQUIRED' }
      }
    }

    try {
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'GET',
        headers: this.getHeaders(requireAuth),
      })

      return this.handleResponse<T>(response)
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Network error',
        code: 'NETWORK_ERROR',
      }
    }
  }

  async post<T>(endpoint: string, body: unknown, requireAuth = false): Promise<ApiResponse<T>> {
    if (!this.config) {
      return { error: 'ApiClient not initialized', code: 'NOT_INITIALIZED' }
    }

    if (requireAuth) {
      const tokenValid = await this.refreshTokenIfNeeded()
      if (!tokenValid) {
        return { error: 'Authentication required', code: 'AUTH_REQUIRED' }
      }
    }

    try {
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(requireAuth),
        body: JSON.stringify(body),
      })

      return this.handleResponse<T>(response)
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Network error',
        code: 'NETWORK_ERROR',
      }
    }
  }

  async delete<T>(endpoint: string, requireAuth = true): Promise<ApiResponse<T>> {
    if (!this.config) {
      return { error: 'ApiClient not initialized', code: 'NOT_INITIALIZED' }
    }

    if (requireAuth) {
      const tokenValid = await this.refreshTokenIfNeeded()
      if (!tokenValid) {
        return { error: 'Authentication required', code: 'AUTH_REQUIRED' }
      }
    }

    try {
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(requireAuth),
      })

      return this.handleResponse<T>(response)
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Network error',
        code: 'NETWORK_ERROR',
      }
    }
  }

  async patch<T>(endpoint: string, body: unknown, requireAuth = true): Promise<ApiResponse<T>> {
    if (!this.config) {
      return { error: 'ApiClient not initialized', code: 'NOT_INITIALIZED' }
    }

    if (requireAuth) {
      const tokenValid = await this.refreshTokenIfNeeded()
      if (!tokenValid) {
        return { error: 'Authentication required', code: 'AUTH_REQUIRED' }
      }
    }

    try {
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method: 'PATCH',
        headers: this.getHeaders(requireAuth),
        body: JSON.stringify(body),
      })

      return this.handleResponse<T>(response)
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Network error',
        code: 'NETWORK_ERROR',
      }
    }
  }
}

export const apiClient = new ApiClient()
